// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
//
// Resolves an array of serial numbers to sparse asset records.
// Used by the confirmed serials modal to link each serial to its asset page.
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { z } from 'zod';

const resolvedAssetSchema = z.object({
  id: z.string().uuid(),
  key: z.string(),
  serial: z.string(),
});

// Schema for the full resolve-serials API response.
const resolveSerialsResponseSchema = z.object({
  data: z.object({
    assets: z.array(resolvedAssetSchema),
    unresolvedSerials: z.array(z.string()),
  }),
  metadata: responseMetadataSchema,
});

type ResolveSerialsResponse = z.infer<typeof resolveSerialsResponseSchema>;

/**
 * Batch-resolve serial numbers to sparse asset records.
 * Serials that do not match any asset are returned in unresolvedSerials.
 *
 * @param serials - Array of serial number strings to resolve
 * @returns The sparse assets matching those serials
 * @throws Re-throws any parsing error from the response schema
 */
export async function resolveSerials(
  serials: string[]
): Promise<ResolveSerialsResponse> {
  const url = '/v1/asset-tools/resolve-serials';
  const response = await api.post(url, { serials });

  return parseResponse(resolveSerialsResponseSchema, response.data, {
    schemaName: 'ResolveSerials',
    endpoint: url,
  });
}
